import { Component, EventEmitter, Input, Output, ViewChild, forwardRef, OnInit, OnDestroy, AfterViewInit } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl } from "@angular/forms";
import { removeCustomQuillMentions } from "@app/helpers";
import { CustomQuillMention } from "@app/models";
import { QuillModules, QuillEditorComponent, ContentChange } from "ngx-quill";
import { Delta } from "quill";
import { Subject, takeUntil } from "rxjs";

@Component({
    selector: 'app-custom-text-editor',
    templateUrl: './custom-text-editor.component.html',
    styleUrls: ['./custom-text-editor.component.scss'],
    standalone: false, 
    providers: [
        {
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => CustomTextEditorComponent),
            multi: true
        }
    ]
})
export class CustomTextEditorComponent implements OnInit, OnDestroy, AfterViewInit, ControlValueAccessor {

    @Input() mentions: CustomQuillMention[] = [];
    @Input() placeholder: string = '';
    @Input() styles: any = {};
    @Input() maxLength: number | null = null;

    @Output() editorFocused: EventEmitter<any> = new EventEmitter<any>();
    @Output() editorBlurred: EventEmitter<any> = new EventEmitter<any>();

    @ViewChild('customQuillEditorRef') customQuillEditorRef!: QuillEditorComponent;

    triggerCharacter: string = '@';
    quillModules: QuillModules = {};
    onFocused: boolean = false;
    editorControl = new UntypedFormControl();
    value: string | null = null;
    isDisabled: boolean = false;
    formControlDirty: boolean = false;
    private readonly unsubscribeAll$: Subject<any> = new Subject();

    // --- ControlValueAccessor Implementation Starts ---

    // Placeholder functions
    onChange: (value: any) => void = () => {};
    onTouched: () => void = () => {};

    ngOnInit() {
        // Listen to changes in the internal control and notify the parent form
        this.editorControl.valueChanges
            .pipe(takeUntil(this.unsubscribeAll$))
            .subscribe(value => {
                this.onChange(value);
                this.onTouched();
            });
    }

    ngAfterViewInit(): void {
        this.customQuillEditorRef?.onFocus
            .pipe(takeUntil(this.unsubscribeAll$))
            .subscribe(() => {
                this.onFocused = true;
                this.editorFocused.emit()
            });
        this.customQuillEditorRef?.onBlur
            .pipe(takeUntil(this.unsubscribeAll$))
            .subscribe(() => {
                this.onFocused = false;
                this.formControlDirty = true;
                this.editorBlurred.emit()
            });
    }

    ngOnDestroy() {
        this.unsubscribeAll$.next(null)
        this.unsubscribeAll$.complete();
    }

    /**
     * Write value from parent form into the internal control
     */
    writeValue(value: any): void {
        // Set value without triggering a 'valueChanges' event
        this.editorControl.setValue(value, { emitEvent: false });
    }

    /**
     * Register the parent form's 'onChange' callback
     */
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    /**
     * Register the parent form's 'onTouched' callback
     */
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    /**
     * Update the internal control's disabled state from the parent form
     */
    setDisabledState?(isDisabled: boolean): void {
        if (isDisabled) {
            this.editorControl.disable({ emitEvent: false });
        } else {
            this.editorControl.enable({ emitEvent: false });
        }
    }

    /**
     * This method is called by the <quill-editor> in the template
     * when its content changes. It updates the internal value
     * and notifies Angular (via onChange) of the new value.
     */
    onContentChanged(event: ContentChange) {
        // Get the full Delta (the editor's entire content)
        const delta = event.editor.getContents();
        this.value = this.convertToString(delta).join('').trimEnd();

        if (!this.formControlDirty) {
            this.formControlDirty = true;
        }
        // Pass this new, correct string to the parent form
        this.onChange(this.value);
    }
    // --- ControlValueAccessor Implementation Ends ---

    constructor() {
        this.quillModules = {
            toolbar: false,
            mention: {
                position: 'bottom',
                allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/, // Regex for valid mention characters
                mentionDenotationChars: [this.triggerCharacter],      // Trigger character
                source: (searchTerm: string, renderList: (matches: CustomQuillMention[], searchTerm: string) => void) => {
                    if (searchTerm.length === 0) {
                        renderList(this.mentions, searchTerm);
                    } else {
                        const matches = this.mentions.filter(mention =>
                            mention.value.toLowerCase().includes(searchTerm.toLowerCase())
                        );
                        renderList(matches, searchTerm);
                    }
                },
            },
            clipboard: {
                matchers: [
                    [Node.TEXT_NODE, (node: any, delta: any) => this.parseTextToDelta(node.data)]
                ]
            }
        };
    }

    private parseTextToDelta(text: string): Delta {
        const newDelta = new Delta();
        if (!text) return newDelta; // Handle null or empty strings
        
        const mentionLookup = new Map(this.mentions.map(m => [m.id, m]));
        const mentionIds = this.mentions.map(m => m.id).join('|');
        // Trailing (?!\d) prevents a shorter id from partial-matching a longer one (e.g. @12 inside @120),
        // which alternation would otherwise pick regardless of id order. 'g' flag for global parsing.
        const mentionRegex = new RegExp(this.triggerCharacter + '(' + mentionIds + ')(?!\\d)', 'g');

        let lastIndex = 0;
        mentionRegex.lastIndex = 0; // Reset regex state
        let match;

        while ((match = mentionRegex.exec(text)) !== null) {
            // Get text before match
            const beforeText = text.substring(lastIndex, match.index);
            if (beforeText.length > 0) newDelta.insert(beforeText);

            // Get mention data
            const mentionId = match[1];
            const mentionData = mentionLookup.get(mentionId);

            if (mentionData) {
                // Insert mention blot
                newDelta.insert({
                mention: {
                    id: mentionData.id,
                    value: mentionData.value,
                    denotationChar: this.triggerCharacter
                }
                });
            } else {
                // Not found, insert as plain text
                newDelta.insert(match[0]);
            }
            lastIndex = mentionRegex.lastIndex;
        }
        
        // Get any remaining text
        const afterText = text.substring(lastIndex);
        if (afterText.length > 0) newDelta.insert(afterText);
        
        return newDelta;
    }

    private convertToString(delta: Delta): string[] {
        // Map over the 'ops' array and convert each part to a string
        return delta.ops.map(op => {
            // Case 1: Plain text
            if (typeof op.insert === 'string') {
                return op.insert;
            }

            // Case 2: A mention blot
            if (op.insert?.['mention']) {
                const mention = op.insert['mention'] as DOMStringMap;
                return `${mention.denotationChar}${mention.id}`; 
            }
            
            // Case 3: Other embeds (like images) or unknown objects.
            // return an empty string to ignore them.
            // (event.text would return '\n' here)
            return ''; 
        });
    }

    private getContentValue(): string {
        if (this.customQuillEditorRef?.quillEditor) {
            const delta = this.customQuillEditorRef.quillEditor.getContents();

            return this.convertToString(delta)?.join('')?.trimEnd() ?? '';
        }
        return '';
    }

    get flattenedValue() {
        const currentValue = this.value ?? this.getContentValue();
        return removeCustomQuillMentions(this.mentions, currentValue, this.triggerCharacter);
    }

}